/**
 * Changing a live SIP's instalment amount for an investor.
 *
 * RAISING the amount is two halves — a replacement mandate and the amount
 * itself — that settle independently, so this reports both rather than one
 * verdict. The asymmetry matters: a mandate that lands with an amount that does
 * not leaves the SIP running at the OLD amount on a NEW mandate, and still
 * consumes one of the five mandate changes the plan is allowed. With none left,
 * a raise is refused up front.
 *
 * LOWERING it fits under the existing mandate, so only the amount half runs —
 * no bank step, and no mandate change spent. The user platform splits the two
 * the same way.
 *
 * The bank authorisation runs in a popup on the same `/mf-payment-return`
 * bridge the rest of the admin MF flows use, so this dialog is never navigated
 * away from.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  MF_MANDATE_CHANGES_EXHAUSTED_REASON,
  MF_MANDATE_CHANGE_LIMIT,
  MF_MODIFICATION_PENDING_REASON,
  completeMFAmountModification,
  fetchMFPlanModifications,
  getMFModificationError,
  hasPendingModification,
  isModificationCompleted,
  isModificationFailed,
  lowerMFSipAmount,
  mfMandateChangesRemaining,
  mfModificationDisabledReason,
  modifyMFSipAmount,
} from "../../Api/mfAdminPlanModification";
import type { MFModificationResult } from "../../Api/mfAdminPlanModification";
import {
  fetchMFSchemeDetails,
  getAdminInvestmentRule,
  getMFErrorMessage,
} from "../../Api/mfAdminInvestments";
import type { MFRecord } from "../../Api/mfAdminInvestments";
import { ensureMFPostbackBridge } from "../../Helpers/mfPostbackBridge";
import { openBlankPopup } from "../../Helpers/mfPopupReturn";
import styles from "../../pages/MutualFunds/user-investments-dashboard.module.css";

const InlineLoader = () => <span className={styles.inlineSpinner} aria-hidden="true" />;

/** Exact, to at most two decimals — admin money is never rounded away. */
const currency = (value: number) => {
  const amount = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const isAmountMultiple = (amount: number, multiple: number) => {
  if (!Number.isFinite(amount) || !Number.isFinite(multiple) || multiple <= 0) return false;
  const quotient = amount / multiple;
  return Math.abs(quotient - Math.round(quotient)) < 1e-8;
};

/** A half the change never needed — the mandate, when the amount went down. */
const isNotRequired = (value: string) =>
  String(value || "").trim().toUpperCase().replace(/[\s-]+/g, "_") === "NOT_REQUIRED";

const statusTone = (value: string) =>
  isModificationCompleted(value) || isNotRequired(value)
    ? styles.statusSuccess
    : isModificationFailed(value)
    ? styles.statusFailed
    : styles.statusPending;

export interface SipModificationDialogProps {
  open: boolean;
  order: MFRecord;
  userUuid: string;
  returnPath: string;
  onClose: () => void;
  onComplete: () => void;
}

const SipModificationDialog: React.FC<SipModificationDialogProps> = ({
  open,
  order,
  userUuid,
  returnPath,
  onClose,
  onComplete,
}) => {
  const planId = String(order.purchase_plan_id || "");
  const currentAmount = Number(order.amount || 0);
  const disabledReason = mfModificationDisabledReason(order);

  const [amount, setAmount] = useState(String(currentAmount || ""));
  const [rule, setRule] = useState<{ minimum: number; maximum: number; multiple: number } | null>(null);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState<MFModificationResult | null>(null);
  const [pendingCheck, setPendingCheck] = useState<"checking" | "clear" | "pending" | "error">(
    "checking"
  );
  const [pendingCheckError, setPendingCheckError] = useState("");
  const pendingCheckRef = useRef(0);

  /**
   * Whether a change to this SIP is still in flight, read fresh from the
   * investor's instruction history. A second change started on top of it would
   * raise another replacement mandate and race the first.
   */
  const isChangePending = useCallback(
    async () => hasPendingModification(await fetchMFPlanModifications(userUuid), planId),
    [userUuid, planId]
  );

  const checkPending = useCallback(async () => {
    const check = ++pendingCheckRef.current;
    setPendingCheck("checking");
    setPendingCheckError("");
    try {
      const pending = await isChangePending();
      if (check === pendingCheckRef.current) setPendingCheck(pending ? "pending" : "clear");
    } catch (requestError) {
      if (check !== pendingCheckRef.current) return;
      setPendingCheck("error");
      setPendingCheckError(
        getMFModificationError(requestError, "This SIP's modification history could not be read.")
      );
    }
  }, [isChangePending]);

  useEffect(() => {
    if (open && !disabledReason) checkPending();
    return () => {
      pendingCheckRef.current += 1;
    };
  }, [open, disabledReason, checkPending]);

  /* The new amount has to clear the scheme's own SIP band, not just be a
     number — the gateway rejects it otherwise, after the mandate is spent. */
  useEffect(() => {
    if (!open) return;
    const isin = String(order.scheme || order.isin || "").trim();
    if (!isin) return;
    let active = true;
    setRulesLoading(true);
    setRulesError("");
    fetchMFSchemeDetails(isin)
      .then((details) => {
        if (!active) return;
        const frequency = String(order.frequency || "monthly").trim().toLowerCase();
        setRule(getAdminInvestmentRule(details, "sip", frequency));
      })
      .catch((requestError) => {
        if (!active) return;
        setRule(null);
        setRulesError(
          getMFErrorMessage(requestError, "The scheme's SIP limits could not be read.")
        );
      })
      .then(() => {
        if (active) setRulesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, order]);

  useEffect(() => {
    if (!open) return;
    setAmount(String(currentAmount || ""));
    setResult(null);
    setError("");
    setMessage("");
  }, [open, currentAmount]);

  const amountNumber = Number(amount);
  // Only a higher instalment needs a new mandate; a lower one fits the old limit.
  const requiresReplacementMandate = Number.isFinite(amountNumber) && amountNumber > currentAmount;
  const changesRemaining = mfMandateChangesRemaining(order);

  const problems = useMemo(() => {
    const list: string[] = [];
    if (disabledReason) list.push(disabledReason);
    if (pendingCheck === "pending") list.push(MF_MODIFICATION_PENDING_REASON);
    if (requiresReplacementMandate && changesRemaining === 0) {
      list.push(MF_MANDATE_CHANGES_EXHAUSTED_REASON);
    }
    if (!Number.isFinite(amountNumber) || amountNumber <= 0) {
      list.push("Enter the new instalment amount.");
    } else if (amountNumber === currentAmount) {
      list.push("The new amount is the same as the current one.");
    } else if (rule) {
      if (amountNumber < rule.minimum) {
        list.push(`The scheme's minimum instalment is ${currency(rule.minimum)}.`);
      }
      if (amountNumber > rule.maximum) {
        list.push(`The scheme's maximum instalment is ${currency(rule.maximum)}.`);
      }
      if (!isAmountMultiple(amountNumber, rule.multiple)) {
        list.push(`The amount must be in multiples of ${currency(rule.multiple)}.`);
      }
    } else if (!rulesLoading) {
      list.push("The scheme's SIP limits are unavailable, so the amount cannot be checked.");
    }
    return list;
  }, [
    disabledReason,
    pendingCheck,
    requiresReplacementMandate,
    changesRemaining,
    amountNumber,
    currentAmount,
    rule,
    rulesLoading,
  ]);

  // Nothing starts until the history says no other change is in flight.
  const canSubmit = !problems.length && !busy && !rulesLoading && pendingCheck === "clear";

  /* A lower instalment fits the existing mandate: no popup, no bank step, and
     none of the five mandate changes spent. */
  const lowerAmount = async () => {
    if (!canSubmit || requiresReplacementMandate) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      setMessage("Checking for a modification already in progress…");
      if (await isChangePending()) {
        setPendingCheck("pending");
        return;
      }
      const outcome = await lowerMFSipAmount({
        purchasePlanId: planId,
        userUuid,
        amount: amountNumber,
        report: setMessage,
      });
      setResult(outcome);
      onComplete();
    } catch (requestError) {
      setError(getMFModificationError(requestError, "The SIP amount could not be lowered."));
    } finally {
      setBusy(false);
      setMessage("");
    }
  };

  /* A higher instalment needs a replacement mandate, authorised at the bank. */
  const raiseAmount = async () => {
    if (!canSubmit || !requiresReplacementMandate) return;
    // Opened inside the click, before any await — a popup opened later is blocked.
    const popup = openBlankPopup(`smest_admin_sip_modify_${planId}`);
    if (!popup) {
      setError(
        "The browser blocked the authorisation window. Allow pop-ups for this site and try again."
      );
      return;
    }
    setBusy(true);
    setError("");
    setResult(null);
    try {
      // Checked again at the moment of starting: another admin may have begun
      // a change since this dialog opened. The popup is still blank here.
      setMessage("Checking for a modification already in progress…");
      if (await isChangePending()) {
        setPendingCheck("pending");
        if (!popup.closed) popup.close();
        return;
      }
      await ensureMFPostbackBridge();
      const outcome = await modifyMFSipAmount(
        {
          purchasePlanId: planId,
          userUuid,
          amount: amountNumber,
          currentMandateId: order.payment_source || order.mandate_details?.id,
          returnPath,
        },
        popup,
        setMessage
      );
      setResult(outcome);
      onComplete();
    } catch (requestError) {
      setError(getMFModificationError(requestError, "The SIP could not be modified."));
      if (!popup.closed) popup.close();
    } finally {
      setBusy(false);
      setMessage("");
    }
  };

  const submit = () => (requiresReplacementMandate ? raiseAmount() : lowerAmount());

  /* The amount did not land, but the mandate either landed or was never needed
     — the one case worth retrying on its own, because it needs no new mandate. */
  const retryAmountOnly = async () => {
    setBusy(true);
    setError("");
    try {
      // A retry while the first amount change is still running would stack a
      // second one on top of it.
      setMessage("Checking the earlier amount change has finished…");
      if (await isChangePending()) {
        setError("The earlier modification is still being processed. Check back in a few minutes.");
        return;
      }
      const amountResult = await completeMFAmountModification({
        purchasePlanId: planId,
        userUuid,
        amount: amountNumber,
        report: setMessage,
      });
      setResult((current) =>
        current
          ? {
              ...current,
              amountStatus: amountResult.status,
              amountFailureReason: amountResult.failureReason,
            }
          : current
      );
      onComplete();
    } catch (requestError) {
      setError(getMFModificationError(requestError, "The amount could not be applied."));
    } finally {
      setBusy(false);
      setMessage("");
    }
  };

  const mandateDone = result ? isModificationCompleted(result.mandateStatus) : false;
  const mandateNotRequired = result ? isNotRequired(result.mandateStatus) : false;
  const amountDone = result ? isModificationCompleted(result.amountStatus) : false;
  const canRetryAmount = Boolean(
    result && (mandateDone || mandateNotRequired) && !amountDone && !busy
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogContent>
        <div className={styles.dialogTitle}>
          <div>
            <h2>Change SIP amount</h2>
            <p>
              {order.scheme_name || order.fund_name || order.scheme || "Mutual fund SIP"} ·
              currently {currency(currentAmount)} · no consent OTP
            </p>
          </div>
          <IconButton size="small" onClick={busy ? undefined : onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className={styles.form}>
          {/* What the entered amount will actually do, and what it costs. */}
          <div className={requiresReplacementMandate ? styles.warning : styles.info}>
            {requiresReplacementMandate ? (
              <>
                Raising the amount needs a <b>replacement e-mandate</b> that the
                investor&apos;s bank must authorise in the window that opens. It
                uses one of the SIP&apos;s five mandate changes, even if the amount
                change then fails.
              </>
            ) : (
              <>
                Lowering the amount keeps the <b>existing mandate</b> — no bank
                authorisation, and none of the SIP&apos;s five mandate changes is
                used. Raising it needs a replacement e-mandate.
              </>
            )}{" "}
            <b>
              {changesRemaining === null
                ? "The SIP did not say how many mandate changes are left."
                : `${changesRemaining} of ${MF_MANDATE_CHANGE_LIMIT} mandate changes left.`}
            </b>
          </div>

          {disabledReason && <div className={styles.error}>{disabledReason}</div>}

          <div className={styles.field}>
            <label>New instalment amount</label>
            <input
              className={styles.input}
              type="number"
              value={amount}
              disabled={busy || Boolean(disabledReason)}
              min={rule?.minimum}
              max={rule?.maximum}
              step={rule?.multiple}
              onChange={(event) => setAmount(event.target.value)}
            />
            {rulesLoading ? (
              <div className={styles.helper}>
                <InlineLoader /> Reading the scheme&apos;s SIP limits…
              </div>
            ) : rule ? (
              <div className={styles.helper}>
                Minimum {currency(rule.minimum)} · Maximum {currency(rule.maximum)} ·
                Multiples of {currency(rule.multiple)}
              </div>
            ) : (
              rulesError && <div className={styles.error}>{rulesError}</div>
            )}
          </div>

          {pendingCheck === "checking" && !disabledReason && !busy && (
            <div className={styles.helper}>
              <InlineLoader /> Checking for a modification already in progress…
            </div>
          )}
          {pendingCheck === "error" && !disabledReason && (
            <div className={styles.error}>
              {pendingCheckError} Until it can be read, a change already in progress on
              this SIP cannot be ruled out, so the amount cannot be changed.{" "}
              <button type="button" className={styles.dialogCancel} onClick={checkPending}>
                Check again
              </button>
            </div>
          )}

          {problems.length > 0 && !disabledReason && (
            <div className={styles.warning}>{problems[0]}</div>
          )}

          {busy && message && (
            <div className={styles.info}>
              <InlineLoader /> {message}
            </div>
          )}
          {error && <div className={styles.error}>{error}</div>}

          {result && (
            <div className={styles.info} style={{ display: "grid", gap: 8 }}>
              <div>
                <b>Replacement mandate</b>{" "}
                <span className={`${styles.status} ${statusTone(result.mandateStatus)}`}>
                  {mandateNotRequired ? "Not required" : result.mandateStatus}
                </span>
                {mandateNotRequired
                  ? " — the existing mandate covers the lower amount"
                  : result.mandateFailureReason
                  ? ` — ${result.mandateFailureReason}`
                  : ""}
              </div>
              <div>
                <b>Instalment amount</b>{" "}
                <span className={`${styles.status} ${statusTone(result.amountStatus)}`}>
                  {result.amountStatus}
                </span>
                {result.amountFailureReason ? ` — ${result.amountFailureReason}` : ""}
              </div>
              <div className={styles.helper}>
                {mandateNotRequired
                  ? amountDone
                    ? "The SIP now debits the lower amount on its existing mandate. No mandate change was used."
                    : `The amount did not change — the SIP still debits ${currency(currentAmount)}. No mandate change was used.`
                  : mandateDone && amountDone
                  ? "Both halves landed — the SIP now debits the new amount."
                  : mandateDone
                  ? `The mandate was replaced but the amount did not change — the SIP still debits ${currency(currentAmount)}. This attempt used one of the five mandate changes.`
                  : "The mandate was not replaced, so the SIP is unchanged."}
              </div>
            </div>
          )}

          <div className={styles.dialogActions}>
            <button className={styles.dialogCancel} onClick={onClose} disabled={busy}>
              {result ? "Done" : "Cancel"}
            </button>
            {canRetryAmount ? (
              <button
                className={styles.dialogSubmit}
                disabled={busy}
                aria-busy={busy || undefined}
                onClick={retryAmountOnly}
              >
                {busy && <InlineLoader />} Retry the amount change
              </button>
            ) : (
              <button
                className={styles.dialogSubmit}
                disabled={!canSubmit || Boolean(result && amountDone)}
                aria-busy={busy || undefined}
                onClick={submit}
              >
                {busy && <InlineLoader />}{" "}
                {busy
                  ? "Modifying…"
                  : requiresReplacementMandate
                  ? "Authorise and raise amount"
                  : "Lower the amount"}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SipModificationDialog;
